import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  updateAgentStatus, 
  fetchAvailabilityTypes, 
  createAgentAvailability 
} from '../../api';

// ─── Paleta KIO (misma que DashboardLayout) ───────────────────────────────────
const T = {
  bg:          '#1e1040',        // fondo principal del panel
  bgHover:     'rgba(134,68,255,0.08)',
  bgActive:    'rgba(134,68,255,0.15)',
  border:      'rgba(134,68,255,0.2)',
  borderStrong:'rgba(134,68,255,0.4)',
  divider:     'rgba(134,68,255,0.12)',
  textPrimary: '#ffffff',
  textMuted:   'rgba(255,255,255,0.4)',
  textLabel:   'rgba(255,255,255,0.28)',
  green:       '#00ff12',
  red:         '#ff5050',
  gray:        '#888888',
  yellow:      '#ffaa00',
  blue:        '#4d9fff',
  purple:      '#8644ff',
};

const STATUS_OPTIONS = [
  { value: 'AVAILABLE', label: 'Disponible', color: T.green },
  { value: 'BUSY',      label: 'Ocupado',    color: T.red,  needsReason: true },
  { value: 'OFF',       label: 'Ausente',    color: T.gray, needsReason: true }
];

// ─── Dot de color ─────────────────────────────────────────────────────────────
function StatusDot({ color, size = 8 }) {
  return (
    <div style={{
      width: size, height: size,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
      boxShadow: `0 0 6px ${color}88`
    }} />
  );
}

// ─── Botón de fila ────────────────────────────────────────────────────────────
function RowButton({ onClick, children, active }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '8px 12px',
        borderRadius: '8px',
        border: 'none',
        background: active ? T.bgActive : hovered ? T.bgHover : 'transparent',
        cursor: 'pointer',
        transition: 'background 0.12s',
        textAlign: 'left'
      }}
    >
      {children}
    </button>
  );
}

// ─── UserStatusDropdown ───────────────────────────────────────────────────────
export default function UserStatusDropdown({ user, onStatusChange }) {
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showReasonModal,       setShowReasonModal]       = useState(false);
  const [selectedStatus,        setSelectedStatus]        = useState(null);
  const [currentStatus,         setCurrentStatus]         = useState('AVAILABLE');
  const [availabilityTypes,     setAvailabilityTypes]     = useState([]);
  const navigate = useNavigate();

  const loadCurrentStatus = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://40.117.79.165:8000'}/workforce/user/${user.id}/current-status`
      );
      if (res.ok) {
        const data = await res.json();
        setCurrentStatus(data.status || 'AVAILABLE');
      }
    } catch (err) {
      console.error('Error loading current status:', err);
    }
  }, [user?.id]);

  const loadAvailabilityTypes = async () => {
    try {
      const types = await fetchAvailabilityTypes();
      setAvailabilityTypes(types);
    } catch (err) {
      console.error('Error loading availability types:', err);
    }
  };

  useEffect(() => {
    loadCurrentStatus();
    loadAvailabilityTypes();
  }, [loadCurrentStatus]);

  const resolveStatus = (status) => {
    const custom = availabilityTypes.find(t => t.availability_type_id === status);
    if (custom) return { label: custom.name, color: custom.blocks_assignment ? T.red : T.yellow };
    const opt = STATUS_OPTIONS.find(o => o.value === status);
    return opt ? { label: opt.label, color: opt.color } : { label: 'Disponible', color: T.green };
  };

  const handleStatusClick = (value) => {
    const opt = STATUS_OPTIONS.find(o => o.value === value);
    if (opt?.needsReason) {
      setSelectedStatus(value);
      setShowReasonModal(true);
    } else {
      handleStatusChange(value, null);
    }
  };

  const handleStatusChange = async (newStatus, reason) => {
    try {
      await updateAgentStatus(user.id, newStatus, reason);
      const res  = await fetch(`${process.env.REACT_APP_API_URL}/workforce/user/${user.id}/current-status`);
      const data = await res.json();
      localStorage.setItem('user', JSON.stringify({ ...user, status: data.status }));
      if (onStatusChange) onStatusChange(data.status);
      setCurrentStatus(data.status);
      setShowReasonModal(false);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Error al actualizar el estado');
    }
  };

  const statusUI = resolveStatus(currentStatus);

  return (
    <>
      {/* ── Panel principal ── */}
      <div style={{
        background: T.bg,
        border: `1px solid ${T.border}`,
        borderRadius: '14px',
        overflow: 'hidden',
        fontFamily: "'Figtree', 'Inter', sans-serif",
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
      }}>

        {/* Estado actual */}
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.divider}` }}>
          <p style={{ fontSize: '10px', fontWeight: 600, color: T.textLabel, letterSpacing: '0.8px', textTransform: 'uppercase', margin: '0 0 6px' }}>
            Estado actual
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <StatusDot color={statusUI.color} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: statusUI.color }}>
              {statusUI.label}
            </span>
          </div>
        </div>

        {/* Cambiar estado */}
        <div style={{ padding: '6px' }}>
          <p style={{ fontSize: '10px', fontWeight: 600, color: T.textLabel, letterSpacing: '0.8px', textTransform: 'uppercase', margin: '4px 8px 6px' }}>
            Cambiar estado
          </p>
          {STATUS_OPTIONS.map(opt => {
            const isCurrentOpt = currentStatus === opt.value;
            return (
              <RowButton key={opt.value} onClick={() => handleStatusClick(opt.value)} active={isCurrentOpt}>
                <StatusDot color={opt.color} />
                <span style={{ flex: 1, fontSize: '13px', fontWeight: isCurrentOpt ? 600 : 400, color: T.textPrimary }}>
                  {opt.label}
                </span>
                {isCurrentOpt && (
                  <div style={{
                    width: '18px', height: '18px', borderRadius: '50%',
                    background: `${opt.color}22`, border: `1px solid ${opt.color}66`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: opt.color }} />
                  </div>
                )}
              </RowButton>
            );
          })}
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: T.divider, margin: '0 6px' }} />

        {/* Programar estado */}
        <div style={{ padding: '6px' }}>
          <RowButton onClick={() => setShowAvailabilityModal(true)}>
            <div style={{
              width: '26px', height: '26px', borderRadius: '7px',
              background: 'rgba(77,159,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              {/* Reloj */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.blue} strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <span style={{ fontSize: '13px', color: T.textPrimary }}>Programar estado</span>
          </RowButton>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: T.divider, margin: '0 6px' }} />

        {/* Perfil y Preferencias */}
        <div style={{ padding: '6px' }}>
          <RowButton onClick={() => navigate('/profile')}>
            <div style={{
              width: '26px', height: '26px', borderRadius: '7px',
              background: 'rgba(134,68,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.purple} strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
            </div>
            <span style={{ fontSize: '13px', color: T.textPrimary }}>Perfil</span>
          </RowButton>

          <RowButton onClick={() => {}}>
            <div style={{
              width: '26px', height: '26px', borderRadius: '7px',
              background: 'rgba(134,68,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.purple} strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </div>
            <span style={{ fontSize: '13px', color: T.textPrimary }}>Preferencias</span>
          </RowButton>
        </div>
      </div>

      {/* ── Modal de Razón ── */}
      {showReasonModal && (
        <ReasonModal
          status={selectedStatus}
          onConfirm={(reason) => handleStatusChange(selectedStatus, reason)}
          onClose={() => { setShowReasonModal(false); setSelectedStatus(null); }}
        />
      )}

      {/* ── Modal de Disponibilidad ── */}
      {showAvailabilityModal && (
        <AvailabilityModal
          user={user}
          onClose={() => setShowAvailabilityModal(false)}
        />
      )}
    </>
  );
}

// ─── Modal de Razón ───────────────────────────────────────────────────────────
function ReasonModal({ status, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const label = status === 'BUSY' ? 'Ocupado' : 'Ausente';

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, fontFamily: "'Figtree', 'Inter', sans-serif"
    }}>
      <div style={{
        background: T.bg,
        border: `1px solid ${T.border}`,
        borderRadius: '16px',
        padding: '24px',
        width: '100%', maxWidth: '400px',
        boxShadow: '0 16px 48px rgba(0,0,0,0.6)'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: T.textPrimary, margin: '0 0 16px' }}>
          Cambiar estado a {label}
        </h3>
        <label style={{ fontSize: '12px', color: T.textMuted, display: 'block', marginBottom: '6px' }}>
          Razón <span style={{ color: T.red }}>*</span>
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="Ej: Reunión con cliente, Almuerzo..."
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'rgba(134,68,255,0.08)',
            border: `1px solid ${T.border}`,
            borderRadius: '10px',
            padding: '10px 12px',
            color: T.textPrimary,
            fontSize: '13px',
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'inherit'
          }}
        />
        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '10px',
              borderRadius: '10px',
              border: `1px solid ${T.border}`,
              background: 'transparent',
              color: T.textMuted,
              cursor: 'pointer', fontSize: '13px', fontWeight: 500
            }}
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              if (!reason.trim()) { alert('Por favor ingresa una razón'); return; }
              onConfirm(reason);
            }}
            style={{
              flex: 1, padding: '10px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #8644ff, #4b2b90)',
              color: '#fff',
              cursor: 'pointer', fontSize: '13px', fontWeight: 600
            }}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de Disponibilidad ──────────────────────────────────────────────────
function AvailabilityModal({ user, onClose }) {
  const [availabilityTypes, setAvailabilityTypes] = useState([]);
  const [formData, setFormData] = useState({
    availability_type_id: '',
    duration_option: 'no_clear',
    custom_end_time: '',
    reason: ''
  });
  const [showCustomEnd, setShowCustomEnd] = useState(false);

  useEffect(() => {
    fetchAvailabilityTypes()
      .then(setAvailabilityTypes)
      .catch(err => console.error('Error loading availability types:', err));
  }, []);

  const handleDurationChange = (option) => {
    setFormData(prev => ({ ...prev, duration_option: option }));
    setShowCustomEnd(option === 'custom');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.duration_option === 'custom' && !formData.custom_end_time) {
      alert('Por favor selecciona una fecha y hora de fin');
      return;
    }
    try {
      await createAgentAvailability({
        user_id: user.id,
        availability_type_id: formData.availability_type_id,
        duration_option: formData.duration_option,
        custom_end_time: formData.custom_end_time,
        reason: formData.reason
      });
      alert('Estado programado correctamente');
      onClose();
      window.location.reload();
    } catch (err) {
      console.error('Error creating availability:', err);
      alert('Error al programar el estado');
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(134,68,255,0.08)',
    border: `1px solid ${T.border}`,
    borderRadius: '10px',
    padding: '10px 12px',
    color: T.textPrimary,
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const labelStyle = {
    fontSize: '11px', fontWeight: 600,
    color: T.textMuted,
    display: 'block', marginBottom: '6px', letterSpacing: '0.3px'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, fontFamily: "'Figtree', 'Inter', sans-serif"
    }}>
      <div style={{
        background: T.bg,
        border: `1px solid ${T.border}`,
        borderRadius: '16px',
        padding: '24px',
        width: '100%', maxWidth: '420px',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 16px 48px rgba(0,0,0,0.6)'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: T.textPrimary, margin: '0 0 20px' }}>
          Programar Estado
        </h3>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Tipo de Estado</label>
            <select
              value={formData.availability_type_id}
              onChange={e => setFormData(prev => ({ ...prev, availability_type_id: e.target.value }))}
              required
              style={inputStyle}
            >
              <option value="">Seleccionar tipo</option>
              {availabilityTypes.map(t => (
                <option key={t.availability_type_id} value={t.availability_type_id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Quitar estado después de...</label>
            <select
              value={formData.duration_option}
              onChange={e => handleDurationChange(e.target.value)}
              required
              style={inputStyle}
            >
              <option value="no_clear">No borrar</option>
              <option value="30min">30 minutos</option>
              <option value="1hour">1 hora</option>
              <option value="4hours">4 horas</option>
              <option value="today">Hoy (fin del día)</option>
              <option value="week">Esta semana (7 días)</option>
              <option value="custom">Elegir fecha y hora</option>
            </select>
          </div>

          {showCustomEnd && (
            <div>
              <label style={labelStyle}>Fecha y hora de fin</label>
              <input
                type="datetime-local"
                value={formData.custom_end_time}
                onChange={e => setFormData(prev => ({ ...prev, custom_end_time: e.target.value }))}
                required
                style={inputStyle}
              />
            </div>
          )}

          <div>
            <label style={labelStyle}>Razón (opcional)</label>
            <textarea
              value={formData.reason}
              onChange={e => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              rows={3}
              placeholder="Ej: Almuerzo, Reunión con cliente..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1, padding: '10px',
                borderRadius: '10px',
                border: `1px solid ${T.border}`,
                background: 'transparent',
                color: T.textMuted,
                cursor: 'pointer', fontSize: '13px', fontWeight: 500
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                flex: 1, padding: '10px',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #8644ff, #4b2b90)',
                color: '#fff',
                cursor: 'pointer', fontSize: '13px', fontWeight: 600
              }}
            >
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}